import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  TextField,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";
import { AnalyticsEvents, analytics } from "common/analytics";
import { useTranslation } from "common/locales";
import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { importSBIToExistingFarm } from "services/sbiImportOperations";
import { useAppStore } from "stores";
import contactSupportImage from "../../assets/images/contact-support.png";
import manualDrawImage from "../../assets/images/manual-draw.png";

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderRadius: 16,
    maxWidth: 480,
    width: "100%",
    margin: theme.spacing(2),
    background: "transparent",
    boxShadow: "none",
  },
}));

const GreenCard = styled(Box)(({ theme }) => ({
  background: "#DBF4C2",
  borderRadius: 16,
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  alignItems: "center",
  textAlign: "center",
  width: "100%",
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(1.5),
  width: "100%",
  fontWeight: 500,
}));

const DigitInput = styled(TextField)(({ theme }) => ({
  width: "2em",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "white",
    borderRadius: 0,
    marginLeft: "-1px",
    "& input": {
      textAlign: "center",
      padding: theme.spacing(1),
      fontSize: "1.5rem",
      width: "100%",
      height: "1.5em",
    },
    "&:hover, &.Mui-focused": {
      zIndex: 1,
    },
  },
}));

const FirstDigitInput = styled(DigitInput)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    marginLeft: 0,
  },
}));

const LastDigitInput = styled(DigitInput)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
  },
}));

const DigitsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  width: "100%",
  justifyContent: "center",
}));

const OptionButton = styled(Button)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(2),
  textAlign: "left",
  backgroundColor: "white",
  color: "black",
  borderRadius: 12,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
  "& .MuiButton-endIcon": {
    marginLeft: "auto",
  },
  "& .MuiButton-startIcon": {
    marginRight: theme.spacing(2),
  },
  "& img": {
    width: 80,
    height: 80,
    borderRadius: 8,
  },
}));

const OptionDescription = styled(Typography)(({ theme }) => ({
  color: "black",
  marginTop: theme.spacing(0.5),
  textTransform: "none",
  fontSize: "0.875rem",
}));

interface NoFieldsModalProps {
  open: boolean;
  onClose: () => void;
}

export default function NoFieldsModal({ open, onClose }: NoFieldsModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Use Zustand for current farm ID
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const [digits, setDigits] = useState(Array(9).fill("")); // Changed to 9 digits
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [showManualOptions, setShowManualOptions] = useState(false);

  const handleImportWithSBI = async () => {
    if (!currentFarmId) {
      setError("No farm selected");
      return;
    }

    setIsLoading(true);
    setError(null);
    analytics.track(AnalyticsEvents.SBI_IMPORT_STARTED);
    try {
      await importSBIToExistingFarm(
        { sbiNumber: parseInt(sbiNumber), farmGuid: currentFarmId },
        queryClient
      );

      analytics.track(AnalyticsEvents.SBI_IMPORT_COMPLETED);
      onClose();
      navigate(`/farm/${currentFarmId}`, { replace: true });
    } catch (error) {
      analytics.track(AnalyticsEvents.SBI_IMPORT_FAILED);
      setError(t("noFieldsModal.import.error"));
      console.error("Failed to import SBI:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrawManually = () => {
    onClose();
    navigate("/add-field/enter-details");
  };

  const handleContactSupport = () => {
    // TODO: Implement support contact functionality
    window.location.href = "mailto:support@ruumi.com";
  };

  const handleDigitChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit
    if (value && !/^\d$/.test(value)) return; // Only allow numbers

    const newDigits = [...digits];
    newDigits[index] = value;
    setDigits(newDigits);

    // Move to next input if value was entered
    if (value && index < 8) {
      // Changed to 8 (last index)
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      // Move to previous input on backspace if current input is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text");

    // Extract only numeric digits from pasted content
    const numericDigits = pastedData.replace(/\D/g, "");

    if (numericDigits.length === 0) return;

    // Create new digits array
    const newDigits = Array(9).fill("");

    // Fill in the digits from pasted content, up to the maximum length
    for (let i = 0; i < Math.min(numericDigits.length, 9); i++) {
      newDigits[i] = numericDigits[i];
    }

    setDigits(newDigits);

    // Focus the next empty input or the last filled input
    const nextEmptyIndex = newDigits.findIndex((digit) => digit === "");
    const focusIndex =
      nextEmptyIndex !== -1
        ? nextEmptyIndex
        : Math.min(numericDigits.length - 1, 8);
    inputRefs.current[focusIndex]?.focus();
  };

  const sbiNumber = digits.join("");

  if (showManualOptions) {
    return (
      <StyledDialog
        open={open}
        onClose={onClose}
        fullScreen={false}
        maxWidth="sm"
        fullWidth
      >
        <GreenCard>
          <Typography
            variant="h6"
            fontWeight="bold"
            component="h1"
            gutterBottom
          >
            {t("noFieldsModal.manual.title")}
          </Typography>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <OptionButton
              onClick={handleDrawManually}
              endIcon={<ChevronRightIcon />}
              startIcon={<img src={manualDrawImage} alt="Manual draw" />}
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: "black" }}
                >
                  {t("noFieldsModal.manual.draw.title")}
                </Typography>
                <OptionDescription variant="body2">
                  {t("noFieldsModal.manual.draw.description")}
                </OptionDescription>
              </Box>
            </OptionButton>
            <OptionButton
              onClick={handleContactSupport}
              endIcon={<ChevronRightIcon />}
              startIcon={
                <img src={contactSupportImage} alt="Contact support" />
              }
            >
              <Box sx={{ textAlign: "left" }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  sx={{ color: "black" }}
                >
                  {t("noFieldsModal.manual.support.title")} (support@ruumi.io)
                </Typography>
                <OptionDescription variant="body2">
                  {t("noFieldsModal.manual.support.description")}
                </OptionDescription>
              </Box>
            </OptionButton>
          </Box>
          <ActionButton
            variant="text"
            color="primary"
            onClick={() => setShowManualOptions(false)}
          >
            {t("noFieldsModal.back")}
          </ActionButton>
        </GreenCard>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      fullScreen={false}
      maxWidth="sm"
      fullWidth
    >
      <GreenCard>
        <Typography variant="h6" component="h1" fontWeight="bold" gutterBottom>
          {t("noFieldsModal.title")}
        </Typography>
        <Typography variant="body1" gutterBottom>
          {t("noFieldsModal.description")}
        </Typography>
        <DigitsContainer>
          {digits.map((digit, index) => {
            const InputComponent =
              index === 0
                ? FirstDigitInput
                : index === digits.length - 1
                ? LastDigitInput
                : DigitInput;
            return (
              <InputComponent
                key={index}
                value={digit}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleDigitChange(index, e.target.value)
                }
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                  handleKeyDown(index, e)
                }
                onPaste={handlePaste}
                inputRef={(el: HTMLInputElement | null) =>
                  (inputRefs.current[index] = el)
                }
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 1,
                }}
              />
            );
          })}
        </DigitsContainer>
        {error && (
          <Typography color="error" variant="body2" gutterBottom align="center">
            {error}
          </Typography>
        )}
        <ActionButton
          variant="contained"
          color="primary"
          onClick={handleImportWithSBI}
          disabled={!sbiNumber || isLoading}
          sx={{ backgroundColor: "#2E7D32" }}
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("noFieldsModal.import.button")
          )}
        </ActionButton>
        <ActionButton
          variant="text"
          color="primary"
          onClick={() => setShowManualOptions(true)}
          disabled={isLoading}
        >
          {t("noFieldsModal.manual.button")}
        </ActionButton>
      </GreenCard>
    </StyledDialog>
  );
}
